import React, { useEffect, useState } from "react";
import L from "leaflet";
import api from "../lib/api";
import { MockBadge, Spinner } from "../components/ui";
import { MapContainer, TileLayer, CircleMarker, Marker, Tooltip as LTooltip, ZoomControl } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { useNavigate } from "react-router-dom";
import { useI18n } from "../i18n/I18nContext";

const PIN_ICON = L.divIcon({
  html: '<div style="width:14px;height:14px;background:#FFD700;border:2.5px solid #fff;border-radius:50%;box-shadow:0 0 8px rgba(255,215,0,0.85)"></div>',
  className: "",
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

// Deterministic jitter so multiple watchlist pins in the same market don't pile up.
// ~±0.025° max offset ≈ ~2 km — readable on any zoom level.
function jitter(id) {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (Math.imul(31, h) + id.charCodeAt(i)) | 0;
  const u = h >>> 0;
  return [((u & 0xff) / 255 - 0.5) * 0.05, (((u >> 8) & 0xff) / 255 - 0.5) * 0.05];
}

export default function MarketMap() {
  const { t } = useI18n();
  const [markets, setMarkets] = useState(null);
  const [watchlist, setWatchlist] = useState([]);
  const [mode, setMode] = useState("lage"); // lage | green_count | absorption
  const nav = useNavigate();

  useEffect(() => {
    api.get("/markets/geo").then(({ data }) => setMarkets(data.markets));
    api.get("/watchlist").then(({ data }) => setWatchlist(data.watchlist || [])).catch(() => {});
  }, []);

  if (!markets) return <div className="p-10"><Spinner /></div>;

  // Build code → {lat,lon} lookup from already-fetched market data
  const coordsByCode = Object.fromEntries(markets.map((m) => [m.code, { lat: m.lat, lon: m.lon }]));

  // Watchlist items that have resolvable market coordinates
  const watchPins = watchlist
    .map((w) => ({ ...w, coords: coordsByCode[w.market_code] }))
    .filter((w) => w.coords);

  return (
    <div className="reveal">
      <div className="px-6 md:px-10 pt-8 pb-5 border-b border-border bg-grid flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline">{t("marketMap.eyebrow", { count: markets.length })}</div>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tight">{t("marketMap.title")}</h1>
          <p className="mt-2 text-xs text-text-muted">{t("marketMap.subtitle")}</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            data-testid="map-mode"
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            className="bg-bg border border-border text-xs font-mono px-3 py-2 text-white"
          >
            <option value="lage">{t("marketMap.modeLage")}</option>
            <option value="green_count">{t("marketMap.modeGreenCount")}</option>
            <option value="absorption">{t("marketMap.modeAbsorption")}</option>
          </select>
          <MockBadge />
        </div>
      </div>

      <div className="relative" style={{ height: "calc(100vh - 49px - 121px)" }}>
        <MapContainer
          center={[48.5, 13.5]}
          zoom={6}
          style={{ height: "100%", width: "100%", background: "#09090B" }}
          scrollWheelZoom={true}
          zoomControl={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          />
          <ZoomControl position="topright" />

          {/* market bubbles */}
          {markets.map((m) => {
            const { color, radius } = visualOf(m, mode);
            return (
              <CircleMarker
                key={m.code}
                center={[m.lat, m.lon]}
                radius={radius}
                pathOptions={{
                  color,
                  fillColor: color,
                  fillOpacity: 0.45,
                  weight: 2,
                }}
                eventHandlers={{
                  click: () => nav(`/markets/${m.code}`),
                }}
              >
                <LTooltip direction="top" offset={[0, -8]} opacity={1} className="!bg-bg !text-white !border-border">
                  <div className="font-mono text-[11px]">
                    <div className="font-bold text-white">{m.name}</div>
                    <div className="mt-0.5">{t("marketMap.tooltipLage", { grade: m.lage_grade, score: m.lage_score })}</div>
                    <div>{t("marketMap.tooltipFaktor", { faktor: m.avg_faktor, cycle: m.cycle })}</div>
                    <div className="text-signal-green">GREEN {m.green_count}</div>
                  </div>
                </LTooltip>
              </CircleMarker>
            );
          })}

          {/* watchlist pins */}
          {watchPins.map((w) => {
            const [jLat, jLon] = jitter(w.id);
            return (
              <Marker
                key={w.id}
                position={[w.coords.lat + jLat, w.coords.lon + jLon]}
                icon={PIN_ICON}
                eventHandlers={{ click: () => nav(`/listings/${w.id}`) }}
              >
                <LTooltip direction="top" offset={[0, -10]} opacity={1} className="!bg-bg !text-white !border-border">
                  <div className="font-mono text-[11px]">
                    <div className="text-[9px] text-signal-yellow uppercase tracking-widest mb-0.5">★ Watchlist</div>
                    <div className="font-bold text-white max-w-[200px] truncate">{w.title}</div>
                    <div className="mt-0.5">
                      Faktor {w.faktor != null ? w.faktor.toFixed(1) : "—"}
                      {" · "}€{(w.price || 0).toLocaleString()}
                      {w.size_m2 ? ` · ${w.size_m2} m²` : ""}
                    </div>
                    <div className="text-text-muted">{w.market_name}</div>
                  </div>
                </LTooltip>
              </Marker>
            );
          })}
        </MapContainer>

        {/* legend */}
        <div className="absolute bottom-4 left-4 z-[400] border border-border bg-bg/90 backdrop-blur p-3 text-[10px] font-mono">
          <div className="overline mb-2">{t("marketMap.legend", { mode: mode === "lage" ? t("marketMap.legendLageGrade") : mode === "green_count" ? t("marketMap.legendGreenDensity") : t("marketMap.legendDemandVelocity") })}</div>
          <div className="space-y-1.5">
            <LegendRow color="#00FF66" label={mode === "lage" ? t("marketMap.legendALage") : t("marketMap.legendHighSignal")} />
            <LegendRow color="#FFCC00" label={mode === "lage" ? "B+/B" : t("marketMap.legendModerate")} />
            <LegendRow color="#FF3333" label={mode === "lage" ? "B−" : t("marketMap.legendWeak")} />
            {watchPins.length > 0 && (
              <LegendRow color="#FFD700" label={`Watchlist (${watchPins.length})`} dot />
            )}
          </div>
        </div>

        {/* market list overlay */}
        <div className="absolute top-4 left-4 z-[400] border border-border bg-bg/90 backdrop-blur p-3 max-w-[280px] hidden lg:block">
          <div className="overline mb-2">{t("marketMap.clickMarker")}</div>
          <div className="text-[10px] font-mono text-text-secondary">
            {t("marketMap.clickMarkerBody")}
          </div>
        </div>
      </div>
    </div>
  );
}

function visualOf(m, mode) {
  if (mode === "lage") {
    const gradeColor = { A: "#00FF66", "B+": "#9AE6B4", B: "#FFCC00", "B-": "#FF3333" };
    return { color: gradeColor[m.lage_grade] || "#FFFFFF", radius: Math.max(8, m.lage_score / 5) };
  }
  if (mode === "green_count") {
    const intensity = Math.min(1, m.green_count / 8);
    return { color: intensity > 0.5 ? "#00FF66" : intensity > 0.25 ? "#FFCC00" : "#FF3333", radius: 6 + intensity * 18 };
  }
  // absorption (inverse — fewer days = stronger demand)
  const score = Math.max(0, 90 - m.absorption_days) / 90;
  return { color: score > 0.6 ? "#00FF66" : score > 0.35 ? "#FFCC00" : "#FF3333", radius: 6 + score * 20 };
}

function LegendRow({ color, label, dot }) {
  return (
    <div className="flex items-center gap-2">
      {dot
        ? <span className="block w-3 h-3 rounded-full border-2 border-white" style={{ background: color }} />
        : <span className="block w-3 h-3 rounded-full" style={{ background: color, opacity: 0.7 }} />
      }
      <span>{label}</span>
    </div>
  );
}
